<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Alert,
		Container,
		Icon,
		IconButton,
		ListNumberCard,
		Text,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { createActionJob } from 'src/helpers/actions/local/core/local_action_helpers';
	import { isSupportedScheduleMethod } from 'src/helpers/actions/schedules/supported/schedule_supported_helpers';
	import {
		getLocalActionPreviewIcon,
		stringifyLocalAction,
	} from 'src/helpers/actions/local/ui/local_actions_view_helpers';
	import { isBluTRV } from 'src/helpers/interface_helpers';
	import { filterCuryAutoHeatingActions } from 'src/components/DevicesSettings/Settings/SettingsG2/Cury/helpers/cury_schedule_filter';
	import { showDeletedCameraZoneAlert } from 'src/helpers/actions/local/ui/camera_local_actions_view';
	//Components
	import LocalActionsModal from '../../LocalActions/LocalActionsModal.svelte';
	//Types
	import type { ActionJob, ScheduleParams } from 'src/types/requests/schedules/schedules_g2';
	import type { Device } from 'src/types/Device';
	import type { Peripheral } from 'src/types/Peripherals';

	export let schedule: ScheduleParams,
		device: Device,
		action: ActionJob,
		error = null,
		switchPeripheral: Peripheral = null;

	let newAction = false,
		currIndex = 0;

	const isFwUpdate = /^Shelly.Update/i.test(schedule?.calls?.[0]?.method);

	// Filter out Cury auto_heating actions for diffuser devices
	$: displayedCalls =
		device.mode === 'diffuser' && schedule?.calls
			? filterCuryAutoHeatingActions(schedule.calls)
			: schedule?.calls || [];

	const onClickAction = (act: ActionJob) => {
		newAction = false;
		action = act;
		// Find the actual index in the original calls array
		currIndex = schedule.calls.indexOf(act);
	};
	const createNewAction = () => {
		newAction = true;
		action = createActionJob(device, switchPeripheral);
	};
	const saveAction = (e: CustomEvent) => {
		if (newAction) {
			schedule.calls.push(e.detail);
		} else {
			schedule.calls[currIndex] = e.detail;
			action = e.detail;
		}
		schedule = schedule;
		action = null;
	};
	const deleteAction = (act: ActionJob) => {
		// Find the actual index in the original calls array
		const actualIndex = schedule.calls.indexOf(act);
		if (actualIndex !== -1) {
			action = null;
			schedule.calls.splice(actualIndex, 1);
			schedule = schedule;
		}
	};
</script>

{#if error?.show && !action && !schedule?.calls?.length}
	<Alert class="mb-m" heading={error.label} type="danger" />
{:else if !isFwUpdate && !isBluTRV(device.type)}
	<Alert
		heading={displayedCalls?.length >= 5
			? $format('alert_warning_heading_actions_limit_reached', {
					default: 'Actions limit reached',
				})
			: $format('alert_info_heading_add_5_actions', {
					default: 'You can add up to 5 actions.',
				})}
		isHeadingBold={displayedCalls?.length >= 5}
		message={displayedCalls?.length >= 5
			? $format('alert_warning_message_actions_limit_description', {
					default:
						'You have reached the limit of 5 actions. If you want to add a new one, please delete one of the actions you have already added.',
				})
			: ''}
		class="mb-m"
		type={displayedCalls?.length >= 5 ? 'warning' : 'info'}
	/>
{/if}
<Container padding={'0'}>
	<Text class="mb-s" styleType="h2">{$format('local_action', { default: 'Local action' })}</Text>
	<Container type="borderDashed">
		{#each displayedCalls as actionItem}
			{#if showDeletedCameraZoneAlert(device, actionItem)}
				<Alert
					class="mb-s"
					heading={$format('camera_zone_deleted_alert', {
						default: 'Camera zone was deleted',
					})}
					type="warning"
				/>
			{/if}
			<ListNumberCard class="mb-s" size="s" editMode>
				<Icon size="s" icon={getLocalActionPreviewIcon(actionItem, device)} class="mr-s" />
				<Text styleType="controls-small" class="width-full" noWrap>
					{@html stringifyLocalAction(actionItem, device)}
				</Text>
				<IconButton
					icon="fas fa-pen"
					class="ml-s"
					disabled={!isSupportedScheduleMethod(actionItem.method)}
					on:click={() => onClickAction(actionItem)}
					aria-label={$format('button_edit', { default: 'Edit' })}
				/>
				<IconButton
					icon="fas fa-trash"
					variant="danger"
					class="ml-s"
					disabled={isFwUpdate}
					on:click={() => deleteAction(actionItem)}
					aria-label={$format('button_delete', { default: 'Delete' })}
				/>
			</ListNumberCard>
		{/each}
		{#if !isFwUpdate}
			<div class="icon-button-wrapper" style="margin: 0 auto;">
				<IconButton
					disabled={displayedCalls?.length >= 5 ||
						(isBluTRV(device.type) && displayedCalls?.length >= 1)}
					on:click={createNewAction}
					icon="fas fa-plus"
					variant="secondary"
				>
					{$format('button_add_local_action', {
						default: 'Add local action',
					})}
				</IconButton>
			</div>
		{/if}
	</Container>
</Container>

{#if action != null}
	<LocalActionsModal
		{action}
		{device}
		{switchPeripheral}
		scheduleAction
		on:save={saveAction}
		on:close={() => {
			action = null;
		}}
	/>
{/if}
